import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';

const priceAlerts = [
  { id: 1, coin: 'BTC', coinName: 'Bitcoin', icon: 'fa-brands fa-bitcoin', color: 'text-crypto-bitcoin', condition: 'above', price: 70000, currentPrice: 67234.56, status: 'watching', createdAt: '2 days ago' },
  { id: 2, coin: 'ETH', coinName: 'Ethereum', icon: 'fa-brands fa-ethereum', color: 'text-crypto-ethereum', condition: 'below', price: 3000, currentPrice: 3245.78, status: 'watching', createdAt: '1 day ago' },
  { id: 3, coin: 'BTC', coinName: 'Bitcoin', icon: 'fa-brands fa-bitcoin', color: 'text-crypto-bitcoin', condition: 'above', price: 65000, currentPrice: 67234.56, status: 'triggered', triggeredAt: '3 hours ago', createdAt: '5 days ago' },
  { id: 4, coin: 'SOL', coinName: 'Solana', icon: 'fa-solid fa-sun', color: 'text-purple-500', condition: 'above', price: 150, currentPrice: 142.56, status: 'watching', createdAt: '12 hours ago' },
  { id: 5, coin: 'ETH', coinName: 'Ethereum', icon: 'fa-brands fa-ethereum', color: 'text-crypto-ethereum', condition: 'above', price: 3200, currentPrice: 3245.78, status: 'triggered', triggeredAt: '1 day ago', createdAt: '3 days ago' },
];

const cryptoOptions = [
  { symbol: 'BTC', name: 'Bitcoin' },
  { symbol: 'ETH', name: 'Ethereum' },
  { symbol: 'SOL', name: 'Solana' },
  { symbol: 'ADA', name: 'Cardano' },
  { symbol: 'DOT', name: 'Polkadot' },
];

export const Alerts = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('price');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newAlert, setNewAlert] = useState({ coin: 'BTC', condition: 'above', price: '' });

  const watchingAlerts = priceAlerts.filter(a => a.status === 'watching').length;
  const triggeredAlerts = priceAlerts.filter(a => a.status === 'triggered').length;

  const portfolioAlerts = [
    { id: 1, type: 'profit', conditionKey: 'dailyProfitExceeds', value: 500, status: 'triggered', triggeredAt: '2h' },
    { id: 2, type: 'loss', conditionKey: 'dailyLossExceeds', value: 1000, status: 'watching' },
    { id: 3, type: 'balance', conditionKey: 'portfolioBelow', value: 100000, status: 'watching' },
  ];

  const alertHistory = [
    { id: 1, messageEn: 'BTC crossed above $65,000', messageDe: 'BTC hat $65.000 überschritten', time: '3h', type: 'price' },
    { id: 2, messageEn: 'Daily profit exceeded $500', messageDe: 'Tagesgewinn hat $500 überschritten', time: '2h', type: 'portfolio' },
    { id: 3, messageEn: 'ETH crossed above $3,200', messageDe: 'ETH hat $3.200 überschritten', time: '1d', type: 'price' },
    { id: 4, messageEn: 'High volatility detected in altcoins', messageDe: 'Hohe Volatilität bei Altcoins erkannt', time: '2d', type: 'market' },
  ];

  return (
    <div className="p-4 lg:p-6 space-y-6 animate-fade-in" data-testid="alerts-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">{t('priceAlerts')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('getNotified')}</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-alert-btn">
              <i className="fa-solid fa-plus mr-2"></i>
              {t('createAlert')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('createAlert')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>{t('cryptocurrency')}</Label>
                <Select value={newAlert.coin} onValueChange={(v) => setNewAlert({...newAlert, coin: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cryptoOptions.map(crypto => (
                      <SelectItem key={crypto.symbol} value={crypto.symbol}>
                        {crypto.name} ({crypto.symbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('condition')}</Label>
                <Select value={newAlert.condition} onValueChange={(v) => setNewAlert({...newAlert, condition: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="above">{t('priceGoesAbove')}</SelectItem>
                    <SelectItem value="below">{t('priceGoesBelow')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('targetPrice')}</Label>
                <Input 
                  type="number" 
                  placeholder="0.00" 
                  value={newAlert.price}
                  onChange={(e) => setNewAlert({...newAlert, price: e.target.value})}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-bell text-primary"></i>
                  <span className="text-sm">{t('pushNotifications')}</span>
                </div>
                <Switch defaultChecked />
              </div>
              <Button className="w-full" onClick={() => setShowCreateDialog(false)}>
                <i className="fa-solid fa-bell mr-2"></i>
                {t('createAlert')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <i className="fa-solid fa-bell text-primary"></i>
              </div>
              <div>
                <p className="text-2xl font-semibold">{priceAlerts.length}</p>
                <p className="text-xs text-muted-foreground">{t('totalAlerts')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center">
                <i className="fa-solid fa-eye text-info"></i>
              </div>
              <div>
                <p className="text-2xl font-semibold">{watchingAlerts}</p>
                <p className="text-xs text-muted-foreground">{t('watching')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <i className="fa-solid fa-check text-success"></i>
              </div>
              <div>
                <p className="text-2xl font-semibold">{triggeredAlerts}</p>
                <p className="text-xs text-muted-foreground">{t('triggered')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <i className="fa-solid fa-chart-line text-warning"></i>
              </div>
              <div>
                <p className="text-2xl font-semibold">{portfolioAlerts.length}</p>
                <p className="text-xs text-muted-foreground">{t('portfolioAlerts')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Alerts List */}
        <div className="lg:col-span-2 space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="price">{t('priceAlerts')}</TabsTrigger>
              <TabsTrigger value="portfolio">{t('portfolioAlerts')}</TabsTrigger>
            </TabsList>

            <TabsContent value="price" className="mt-4 space-y-3">
              {priceAlerts.map(alert => (
                <Card key={alert.id} className={cn(
                  "transition-all",
                  alert.status === 'triggered' && "border-success/50 bg-success/5"
                )}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center",
                          alert.coin === 'BTC' ? 'bg-crypto-bitcoin/15' :
                          alert.coin === 'ETH' ? 'bg-crypto-ethereum/15' : 'bg-purple-500/15'
                        )}>
                          <i className={cn(alert.icon, alert.color)}></i>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{alert.coinName}</p>
                            <Badge variant="secondary" className="text-xs">{alert.coin}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {alert.condition === 'above' ? t('priceAbove') : t('priceBelow')}{' '}
                            <span className="font-mono">${alert.price.toLocaleString()}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm font-mono">${alert.currentPrice.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">{t('current')}</p>
                        </div>
                        {alert.status === 'triggered' ? (
                          <Badge variant="success">
                            <i className="fa-solid fa-check mr-1"></i>
                            {t('triggered')}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <i className="fa-solid fa-eye mr-1"></i>
                            {t('watching')}
                          </Badge>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <i className="fa-solid fa-trash text-muted-foreground text-sm"></i>
                        </Button>
                      </div>
                    </div>
                    {alert.status === 'triggered' && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs text-success">
                          <i className="fa-solid fa-clock mr-1"></i>
                          {t('triggered')} {alert.triggeredAt}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="portfolio" className="mt-4 space-y-3">
              {portfolioAlerts.map(alert => (
                <Card key={alert.id} className={cn(
                  "transition-all",
                  alert.status === 'triggered' && "border-success/50 bg-success/5"
                )}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          alert.type === 'profit' ? 'bg-success/10' :
                          alert.type === 'loss' ? 'bg-destructive/10' : 'bg-primary/10'
                        )}>
                          <i className={cn(
                            alert.type === 'profit' ? 'fa-solid fa-arrow-trend-up text-success' :
                            alert.type === 'loss' ? 'fa-solid fa-arrow-trend-down text-destructive' :
                            'fa-solid fa-wallet text-primary'
                          )}></i>
                        </div>
                        <div>
                          <p className="font-medium">{t(alert.conditionKey)}</p>
                          <p className="text-sm text-muted-foreground font-mono">${alert.value.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {alert.status === 'triggered' ? (
                          <Badge variant="success">
                            <i className="fa-solid fa-check mr-1"></i>
                            {t('triggered')}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <i className="fa-solid fa-eye mr-1"></i>
                            {t('watching')}
                          </Badge>
                        )}
                        <Switch defaultChecked={alert.status === 'watching'} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar - Alert History */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('alertHistory')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {alertHistory.map(item => (
                <div key={item.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
                    item.type === 'price' ? 'bg-primary/10' :
                    item.type === 'portfolio' ? 'bg-success/10' : 'bg-warning/10'
                  )}>
                    <i className={cn(
                      "text-xs",
                      item.type === 'price' ? 'fa-solid fa-chart-line text-primary' :
                      item.type === 'portfolio' ? 'fa-solid fa-wallet text-success' :
                      'fa-solid fa-triangle-exclamation text-warning'
                    )}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{language === 'de' ? item.messageDe : item.messageEn}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.time}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Quick Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('notificationSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-bell text-sm text-muted-foreground"></i>
                  <span className="text-sm">{t('pushNotifications')}</span>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-envelope text-sm text-muted-foreground"></i>
                  <span className="text-sm">{t('emailAlerts')}</span>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-mobile text-sm text-muted-foreground"></i>
                  <span className="text-sm">{t('smsAlerts')}</span>
                </div>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-volume-high text-sm text-muted-foreground"></i>
                  <span className="text-sm">{t('sound')}</span>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Alerts;
